import { Stack } from '@mantine/core'
import { ReactNode, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { getLoanLoader, getLoansByFacility, getLockedLoansByFacility } from 'app/state/ducks/loans/selectors'
import { loadOneDeal } from 'app/state/ducks/deals/thunks'
import { getDeal, getDeals } from 'app/state/ducks/deals/selectors'
import { DealParams } from 'app/models/deal-params'
import { getEntities } from 'app/state/ducks/entities/selectors'
import ExpandableMantineTable from './table-loan'
import { LoanParams } from 'app/models/loan-params'
import { DataBoxLayout } from 'app/views/components/detailSections/data-box-layout'
import { buildFacilityDetailSectionForFacilityTabs } from 'app/views/components/functions/buildDetailsObjects'
import { useNavigate } from 'react-router'
import { DetailSectionHeader } from 'app/views/components/detailSections/detail-section-header'
import { DetailSection } from 'app/views/components/detailSections/detail-section'
import { getLoanAmendmentsByFacility, getFacilityLoanAmendmentLoader } from 'app/state/ducks/loan-amendments/selectors'
import BulkActionsButton from 'app/views/components/buttons/bulk-actions-button'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import MergeLoansModal from 'app/views/pages/deals-management/merges/merge-loans-modal'
import IonIcon from '@reacticons/ionicons'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import { canMergeLoans, getMergeLoansErrorMessage } from 'app/views/pages/deals-management/loans/utilities/merge-loan-validation'

export default function LoansPage(): JSX.Element {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [deal, setDeal] = useState<DealParams | undefined>(undefined)
    const facility = useSelector(getFacility)
    const deals = useSelector(getDeals)
    const currentDeal = useSelector(getDeal)
    const entities = useSelector(getEntities)
    const indexRateOptions = useSelector(getIndexRateOptions)

    // Bulk actions state
    const [selectedBulkLoans, setSelectedBulkLoans] = useState<LoanParams[]>([])
    const [tableSelectedRows, setTableSelectedRows] = useState<Record<string, boolean>>({})

    const loanData = useSelector(getLoansByFacility);
    const loanAmendmentData = useSelector(getLoanAmendmentsByFacility)
    const loanAmendmentLoader = useSelector(getFacilityLoanAmendmentLoader)
    const loans = loanData?.data ?? [];
    const loanAmendments = loanAmendmentData?.data ?? [];

    // Combine loans and amendments, prioritizing amendments when IDs match (similar to DealManagementPage)
    const data = useMemo(() => {
        return loans.map(loan => {
            const amendment = loanAmendments.find(amendment => amendment.id === loan.id)
            if (amendment) {
                return amendment  // Use the amendment directly, which already has amendmentType
            }
            return loan
        }).concat(loanAmendments.filter(amendment => !loans.some(loan => loan.id === amendment.id)))
    }, [loans, loanAmendments])
    const { data: inactiveData, loading: inactiveLoading } = useSelector(getLockedLoansByFacility);
    const [combinedLoans, setCombinedLoans] = useState<LoanParams[]>([]); // New combined state
    const [swinglineLoans, setSwinglineLoans] = useState<LoanParams[]>([]);
    const [nonSwinglineLoans, setNonSwinglineLoans] = useState<LoanParams[]>([]);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [mergeLoansModalOpen, setMergeLoansModalOpen] = useState(false);
    const [selectedLoansForModal, setSelectedLoansForModal] = useState<LoanParams[]>([]);
    const loanLoader = useSelector(getLoanLoader);

    // Combine `loanLoader`, `loanAmendmentLoader` and `inactiveLoading` into `globalLoading`
    useEffect(() => {
        setGlobalLoading(loanLoader || loanAmendmentLoader || inactiveLoading);
    }, [loanLoader, loanAmendmentLoader, inactiveLoading]);
    // Combine `data` (loans + amendments) and `inactiveData` into a single array
    useEffect(() => {
        const combined = [...data, ...(inactiveData ?? [])]; // Merge active loans/amendments and inactive loans
        setCombinedLoans(combined);
    }, [data, inactiveData]);

    // Filter combined loans into swingline and non-swingline categories
    useEffect(() => {
        setSwinglineLoans(combinedLoans.filter((loan) => loan.isSwingLine));
        setNonSwinglineLoans(combinedLoans.filter((loan) => !loan.isSwingLine));
    }, [combinedLoans]);

    useEffect(() => {
        if (!facility || !deals) {
            return
        }
        const deal = deals.find(deal => deal.id === facility.dealId.id)
        if (deal) {
            setDeal(deal)
        } else {
            dispatch(loadOneDeal(facility.dealId.id, 'Terminated'))
        }
    }, [facility, deals])

    useEffect(() => {
        if (!currentDeal) {
            return
        }
        setDeal(currentDeal)
    }, [currentDeal])

    const facilityDetails = buildFacilityDetailSectionForFacilityTabs(facility, entities, deal, navigate)

    // Logic for determining whether selected loans can be merged using utility function
    const canMergeSelectedLoans = useMemo(() => {
        return canMergeLoans(selectedBulkLoans, indexRateOptions)
    }, [selectedBulkLoans, indexRateOptions])

    const bulkLoanActions = {
        'Merge Loans': () => {
            // Store the selected loans before they get cleared
            const loansToMerge = [...selectedBulkLoans];

            if (canMergeSelectedLoans) {
                setMergeLoansModalOpen(true);

                // Set the loans for the modal after a small delay to ensure modal is open
                setTimeout(() => {
                    setSelectedLoansForModal(loansToMerge);
                }, 0);
            } else {
                const errorMessage = getMergeLoansErrorMessage(selectedBulkLoans, indexRateOptions);
                if (errorMessage) {
                    ErrorNotification({
                        title: 'Cannot Merge Loans',
                        message: errorMessage,
                    });
                }
            }
        }
    };

    const bulkLoanActionIcons = {
        'Merge Loans': <IonIcon className="action-ion-icon" name="settings-outline" />
    };

    const clearSelectedItems = () => {
        setSelectedBulkLoans([]);
        setTableSelectedRows({}); // clear the table selection state
    };

    const renderTableSection = (headerText: string, tableSection: ReactNode, showBulkActions: boolean = false) => {

        const bulkActionsButton = showBulkActions ? (
            <BulkActionsButton
                selectedItems={selectedBulkLoans}
                actions={bulkLoanActions}
                clearSelectedItems={clearSelectedItems}
                menuIcons={bulkLoanActionIcons}
            />
        ) : undefined;

        return (
            <>
                <DetailSectionHeader headerText={headerText} additionalContent={bulkActionsButton} />
                {tableSection}
            </>
        )
    }

    const renderDetailSection = (headerText: string, detailObjArr: any) => (
        <>
            <DetailSectionHeader headerText={headerText} />
            <DetailSection detailObjArr={detailObjArr} columnCount={4} />
        </>
    )


    return (
        <Stack w="100%">
            <DataBoxLayout
                tableSections={[
                    renderTableSection('Loans Within Facility',
                        <ExpandableMantineTable
                            loans={nonSwinglineLoans}
                            globalLoading={globalLoading}
                            setSelectedBulkLoans={setSelectedBulkLoans}
                            tableSelectedRows={tableSelectedRows}
                            setTableSelectedRows={setTableSelectedRows}
                        />,
                        true // Show bulk actions for main loans  TODO: REVERT BACK TO TRUE ONCE READY TO GO LIVE WITH LOAN MERGE FUNCTIONALITY (AFTER FACILITY HISTORY FIXES ARE IMPLEMENTED)
                    ),
                    renderTableSection('Swingline Loans Within Facility',
                        <ExpandableMantineTable
                            loans={swinglineLoans}
                            globalLoading={globalLoading}
                            setSelectedBulkLoans={setSelectedBulkLoans}
                            tableSelectedRows={tableSelectedRows}
                            setTableSelectedRows={setTableSelectedRows}
                        />,
                        false // No bulk actions for swingline loans
                    )
                ]}
                sections={[
                    [renderDetailSection('Details', facilityDetails)]
                ]}
                tablePosition='top'
            />
            <MergeLoansModal
                key={selectedLoansForModal.map(loan => loan.id).join(',')}
                open={mergeLoansModalOpen}
                setOpen={(open) => {
                    setMergeLoansModalOpen(open);
                    if (!open) {
                        setSelectedLoansForModal([]); // Clear modal loans when closing
                    }
                }}
                selectedLoans={selectedLoansForModal}
            />
        </Stack >
    )
}